import React from 'react';
import { IMG, Reveal, Overline } from './shared';

export const PowerSection = () => (
  <section className="bg-[#0a0a0a] text-white py-32 md:py-44 px-6 md:px-12 overflow-hidden" data-testid="hv2-power">
    <div className="max-w-7xl mx-auto text-center">
      <Reveal>
        <p className="font-oswald font-semibold text-[18vw] lg:text-[12rem] leading-none tracking-tight text-white select-none">
          15,000<span className="text-[#00AEEF]">Pa</span>
        </p>
      </Reveal>
      <Reveal delay={150}>
        <h2 className="font-outfit text-3xl md:text-5xl font-medium tracking-tight mt-10">POWER THAT PICKS UP MORE.</h2>
      </Reveal>
      <Reveal delay={250}>
        <p className="font-manrope text-lg text-white/50 mt-6 max-w-xl mx-auto leading-relaxed">
          Up to 15,000Pa suction engineered for powerful everyday floor cleaning — fine dust, pet hair and debris deep in carpet fibres.
        </p>
      </Reveal>
    </div>
  </section>
);

export const IntelligenceSection = () => (
  <section className="bg-[#0a0a0a] text-white py-28 md:py-40 px-6 md:px-12 border-t border-white/10" data-testid="hv2-intelligence">
    <div className="max-w-5xl mx-auto">
      <Reveal><Overline dark>LiDAR + AI</Overline></Reveal>
      <Reveal delay={100}>
        <h2 className="font-outfit text-4xl md:text-6xl lg:text-7xl font-medium leading-[1.05] tracking-tighter">
          IT DOESN'T JUST MOVE.<br />
          <span className="text-white/40">IT UNDERSTANDS THE ROOM.</span>
        </h2>
      </Reveal>
      <Reveal delay={200}>
        <div className="mt-14 flex flex-col md:flex-row md:items-end gap-8">
          <p className="font-oswald text-6xl md:text-7xl font-medium text-[#00AEEF] leading-none">360°<span className="block font-outfit text-lg md:text-xl text-white/60 tracking-[0.2em] mt-3">LASER MAPPING</span></p>
          <p className="font-manrope text-lg text-white/50 max-w-md leading-relaxed">
            Intelligent mapping helps LumineX 300 understand its surroundings and plan systematic cleaning routes.
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);

export const VacuumMopSection = () => (
  <section className="bg-white text-gray-900 py-28 md:py-40 px-6 md:px-12" data-testid="hv2-vacuum-mop">
    <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-center">
      <div>
        <Reveal><Overline>Vacuum + Mop</Overline></Reveal>
        <Reveal delay={100}>
          <h2 className="font-outfit text-5xl md:text-7xl font-medium leading-[1.02] tracking-tighter">
            TWO JOBS.<br />ONE ROBOT.
          </h2>
        </Reveal>
        <Reveal delay={200}>
          <p className="font-outfit text-xl md:text-2xl text-[#0057B8] font-medium mt-8 tracking-tight">VACUUM + MOP, TOGETHER.</p>
        </Reveal>
        <Reveal delay={300}>
          <p className="font-manrope text-lg text-gray-500 mt-4 max-w-md leading-relaxed">
            LumineX 300 vacuums and mops simultaneously to simplify everyday floor cleaning — and keeps off carpets while it mops.
          </p>
        </Reveal>
      </div>
      <Reveal delay={200}>
        <img src={IMG.mopFloor} alt="Harpalyk SMARTBOT LumineX 300 robot vacuum and mop cleaning a modern home floor" loading="lazy" decoding="async" className="w-full h-[420px] md:h-[520px] object-cover" />
      </Reveal>
    </div>
  </section>
);

export const SelfEmptySection = () => (
  <section className="bg-[#f5f5f7] text-gray-900 py-28 md:py-40 px-6 md:px-12" data-testid="hv2-self-empty">
    <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-center">
      <Reveal className="order-2 lg:order-1">
        <img src={IMG.robotDock} alt="Harpalyk LumineX 300 docking at its 3L self-emptying station" loading="lazy" decoding="async" className="w-full max-w-lg mx-auto rounded-3xl shadow-xl" />
      </Reveal>
      <div className="order-1 lg:order-2">
        <Reveal><Overline>3L Self-Emptying Dock</Overline></Reveal>
        <Reveal delay={100}>
          <h2 className="font-outfit text-5xl md:text-7xl font-medium leading-[1.02] tracking-tighter">
            CLEAN MORE.<br /><span className="text-gray-400">EMPTY LESS.</span>
          </h2>
        </Reveal>
        <Reveal delay={200}>
          <p className="font-oswald text-6xl md:text-8xl font-medium text-[#0057B8] mt-10 leading-none">
            <span className="font-outfit block text-sm md:text-base tracking-[0.3em] text-gray-400 mb-2">UP TO</span>
            70 DAYS
          </p>
        </Reveal>
        <Reveal delay={300}>
          <p className="font-manrope text-lg text-gray-500 mt-6 max-w-md leading-relaxed">
            The 3L self-emptying dock automatically collects dust from LumineX 300, reducing how frequently the dust container needs emptying.
          </p>
        </Reveal>
      </div>
    </div>
  </section>
);

export const CoverageHepaSection = () => (
  <section className="bg-white text-gray-900 py-28 md:py-40 px-6 md:px-12 border-t border-black/5" data-testid="hv2-coverage-hepa">
    <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-20">
      <div>
        <Reveal><Overline>Coverage</Overline></Reveal>
        <Reveal delay={100}>
          <p className="font-oswald text-6xl md:text-8xl font-medium leading-none">
            <span className="font-outfit block text-sm md:text-base tracking-[0.3em] text-gray-400 mb-2">UP TO</span>
            3,000 <span className="text-3xl md:text-5xl align-top">SQ.FT.</span>
          </p>
        </Reveal>
        <Reveal delay={200}>
          <h2 className="font-outfit text-3xl md:text-4xl font-medium tracking-tight mt-8">ROOM AFTER ROOM.</h2>
        </Reveal>
        <Reveal delay={300}>
          <p className="font-manrope text-lg text-gray-500 mt-4 max-w-md leading-relaxed">
            Designed for substantial floor areas with intelligent mapping and systematic navigation.
          </p>
        </Reveal>
      </div>
      <div className="md:border-l md:border-black/5 md:pl-20">
        <Reveal><Overline>Filtration</Overline></Reveal>
        <Reveal delay={100}>
          <p className="font-oswald text-6xl md:text-8xl font-medium leading-none">HEPA</p>
        </Reveal>
        <Reveal delay={200}>
          <h2 className="font-outfit text-3xl md:text-4xl font-medium tracking-tight mt-8">CLEANER AIRFLOW.<br />SMARTER FILTRATION.</h2>
        </Reveal>
        <Reveal delay={300}>
          <p className="font-manrope text-lg text-gray-500 mt-4 max-w-md leading-relaxed">
            HEPA filtration captures fine dust and allergens as LumineX 300 cleans — for noticeably fresher indoor air.
          </p>
        </Reveal>
      </div>
    </div>
  </section>
);
